import PropTypes from "prop-types";
import React from "react";

import useOutputReportStore from "../../../../store/OutputReport.store";
import { getOutputType } from "../../../../utils/helpers/dataTable";
import BarChart from "../../../Chart/Bar";

import {
  createChartLegend,
  getChartOptions,
  getColorSequence,
  getDatasetsMinMaxValueZeroCenter,
  getGroupedBarXlabels,
  getReversedChartDataset,
  getStepSizeAndLabelsMinMax,
  getXlabels,
  getYlabels,
} from "../../../../utils/helpers/chart";

const ScenarioAnalysisChart = ({ id, isFullWidth }) => {
  const reportName = "Scenario Analysis";
  const data = useOutputReportStore((state) => state.data);
  const { reportType } = getOutputType(data);

  const barChartId = id;

  // Stacked Bar Options
  const config = {
    transition: {
      barThickness: 18,
    },
    cash: {
      isFullWidth,
      xAnnotationSpace: 0,
      borderRadius: 8,
      isStacked: true,
    },
  };

  const options = {
    yTicksType: "percent",
    yTitle: "Total Return",
    xAnnotationType: "type-1",
    isXannotationCentered: true,
    xLabels: getGroupedBarXlabels(reportName, data),
    isStacked: reportType === "cash",
    tooltipValueType: "percent",
    ...config[reportType],
  };

  const reportData = data[reportName];
  const isDate = false;
  const yLabels = getYlabels(reportData, []);
  const excludedXlabels =
    reportType === "cash"
      ? ["Market Value ($)", "Effective Duration"]
      : ["Model", "Reference Index"];
  const { xLabels } = getXlabels(reportData, excludedXlabels);

  // Stacked Bar Styles
  const withGray = reportType === "transition";
  const stackedBarStyles = getColorSequence(withGray);

  // Stacked Bar Data - Y Axis Data
  let groupedBarDatasets = [];
  if (reportType === "transition") {
    groupedBarDatasets = getReversedChartDataset(
      reportData,
      yLabels,
      xLabels,
      isDate
    );
  } else if (reportType === "cash") {
    const reversedDataset = getReversedChartDataset(
      reportData,
      yLabels,
      xLabels,
      isDate
    );

    const positiveData = reversedDataset[0].data.map((value) =>
      value > 0 ? value : 0
    );
    const negativeData = reversedDataset[0].data.map((value) =>
      value < 0 ? value : 0
    );
    groupedBarDatasets = [
      {
        label: "Positive Return",
        data: positiveData,
      },
      {
        label: "Negative Return",
        data: negativeData,
      },
    ];
  }

  // get dataset max
  const { min, max } = getDatasetsMinMaxValueZeroCenter(groupedBarDatasets);
  //get step size and step labels
  const { stepSize, stepSizeLabels } = getStepSizeAndLabelsMinMax(min, max);

  // Chart Option
  options.yTicksMin = min;
  options.yTicksMax = max;
  options.yStepSize = stepSize;
  options.yStepSizeLabels = stepSizeLabels;

  const stackedBarOptions = getChartOptions(options);

  // Format Chart Data
  const groupedBarData = {
    labels: yLabels,
    datasets: groupedBarDatasets.map((item, idx) => {
      return {
        ...item,
        ...stackedBarStyles[idx],
      };
    }),
  };

  // Legend for Scenario Analysis
  const asteriskedLabels = { "Maintain and Reinvest Proceeds": "*" };
  const stackedBarCustomLegend = createChartLegend({
    containerId: barChartId,
    legendId: `legend-${barChartId}`,
    legendTitle: "",
    isReversed: false,
    isVerticalCentered: reportType === "cash",
    asteriskedLabels: reportType === "transition" ? asteriskedLabels : {},
  });

  return (
    <BarChart
      id={barChartId}
      data={groupedBarData}
      options={stackedBarOptions}
      plugins={[stackedBarCustomLegend]}
      ariaLabel="Scenario Analysis Report Bar Chart"
      height={isFullWidth ? "470px" : "372px"}
    />
  );
};

ScenarioAnalysisChart.propTypes = {
  id: PropTypes.string,
  isFullWidth: PropTypes.bool,
};

ScenarioAnalysisChart.defaultProps = {
  id: "bar-chart-scenario-analysis",
  isFullWidth: true,
};

export default ScenarioAnalysisChart;
